'use client';

import { CustomTooltip } from '@/src/components/layout/CustomTooltip';
import { Icon } from '@/src/components/ui';
import PageLoader from '@/src/components/ui/PageLoader';
import {
  useAchievementsQuery,
  useEditAchievementMutation,
} from '@/src/hooks/use-achievements';
import { cn } from '@workspace/ui/lib/utils';
import { useState } from 'react';
import { IAchievement } from 'types/src/types';

type Props = {
  isSettingsPage?: boolean;
  profileAchievements?: IAchievement[];
};

export const Achievements = ({ isSettingsPage = false, profileAchievements }: Props) => {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const { data: achievements = [], isLoading } = useAchievementsQuery();
  const editMutation = useEditAchievementMutation();

  const handleToggleVisibility = (achievement: IAchievement) => {
    editMutation.mutate({
      id: achievement.id,
      isVisible: !achievement.isVisible,
    });
  };

  if (!isSettingsPage) {
    if (!profileAchievements || profileAchievements.length === 0) return null;
    const visible = profileAchievements?.filter((a) => a.isVisible);
    if (visible?.length === 0) return null;

    return (
      <div className="p-0 md:px-2 px-3">
        <h2 className="md:text-lg! text-sm! font-normal text-black md:pl-0 pl-3">
          Achievements
        </h2>
        <div className="flex md:gap-2 gap-0.5 mt-2 md:pl-0 pl-3">
          {visible?.slice(0, 3).map((achievement) => (
            <div
              key={achievement.id}
              className="text-sm w-24 flex flex-col items-center relative"
            >
              <div
                className={cn(
                  'bg-bg-gray p-1.5 w-fit rounded-sm relative transition-all duration-200 group',
                  {
                    'rounded-full': achievement.id !== 2,
                  }
                )}
              >
                <img
                  src={achievement.iconUrl}
                  alt={achievement.title}
                  className="md:size-14 size-10 object-cover"
                />
              </div>

              <CustomTooltip
                label={achievement.description ?? 'No description'}
                placement="bottom"
              >
                <span
                  className={cn(
                    'mt-2 text-center leading-tight hover:cursor-default break-words line-clamp-2 md:text-sm text-xs'
                  )}
                >
                  {achievement.title}
                </span>
              </CustomTooltip>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {isLoading && <PageLoader />}
      <h2 className="text-base! font-normal text-black/40 mb-3">
        Your achievements
      </h2>

      {(achievements.length === 0 && !isLoading) ? (
        <div className="md:px-4 md:py-3 bg-bg-gray rounded-lg flex items-center text-sm text-black gap-3 w-fit">
          <Icon name="hint" className="size-5" />
          Add a code to receive a new achievement or reward!
        </div>
      ) : (
        <div className="flex flex-wrap gap-6">
          {achievements.map((achievement) => {
            const isHovered = hoveredId === achievement.id;

            const isRowLoading =
              editMutation.isPending &&
              editMutation.variables?.id === achievement.id;

            const visibilityLabel = achievement.isVisible
              ? 'Visible on profile'
              : 'Hidden on profile';

            return (
              <div
                key={achievement.id}
                className="text-sm w-24 flex flex-col items-center relative"
              >
                <CustomTooltip label={visibilityLabel} placement="top">
                  <div
                    className={cn(
                      'bg-bg-gray p-1.5 w-fit rounded-sm relative cursor-pointer transition-all duration-200 group',
                      {
                        'rounded-full': achievement.id !== 2,
                        'opacity-50': isRowLoading,
                        'pointer-events-none': isRowLoading,
                      }
                    )}
                    onMouseEnter={() => setHoveredId(achievement.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => handleToggleVisibility(achievement)}
                  >
                    <img
                      src={achievement.iconUrl}
                      alt={achievement.title}
                      className="size-14 object-cover"
                    />
                    <div
                      className={cn(
                        'absolute inset-0 bg-black/50 flex items-center justify-center rounded-sm transition-opacity duration-200 pointer-events-none',
                        {
                          'rounded-full': achievement.id !== 2,
                          'opacity-0 group-hover:opacity-100': !isRowLoading,
                          'opacity-100': isRowLoading,
                        }
                      )}
                    >
                      <Icon name={achievement.isVisible ? "eye" : "eye-off"} className="size-6 text-white" />
                    </div>
                  </div>
                </CustomTooltip>

                <CustomTooltip
                  label={achievement.description ?? 'No description'}
                  placement="bottom"
                >
                  <span
                    className={cn(
                      'mt-2 text-center text-sm leading-tight hover:cursor-default break-words',
                      'line-clamp-2'
                    )}
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {achievement.title}
                  </span>
                </CustomTooltip>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

